/**
 * UserAvatar — küçük yeniden kullanılabilir avatar
 *
 * `photoUrl` varsa yükler; başarısız / boşsa gradient renk + initials fallback.
 * Radix Avatar under the hood — image error → fallback otomatik switch.
 *
 * Props:
 *   name     — kullanıcı adı (initials için)
 *   email    — name yoksa fallback
 *   photoUrl — /api/users/{id}/photo?v=... path'i (aynı origin, cookie authenticate)
 *   size     — "xs" 24px | "sm" 32px | "md" 40px | "lg" 56px (varsayılan "sm")
 */
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

const SIZE_CLASS = {
  xs: "h-6 w-6 text-[9px]",
  sm: "h-8 w-8 text-[11px]",
  md: "h-10 w-10 text-xs",
  lg: "h-14 w-14 text-base",
};

// Aynı email/name için deterministik pastel gradient — göze güzel + tanınabilir
const GRADIENTS = [
  "from-violet-500 to-fuchsia-500",
  "from-sky-500 to-cyan-500",
  "from-emerald-500 to-teal-500",
  "from-amber-500 to-orange-500",
  "from-rose-500 to-pink-500",
  "from-indigo-500 to-blue-500",
  "from-lime-500 to-green-500",
  "from-purple-500 to-violet-500",
];

function hash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h << 5) - h + str.charCodeAt(i);
  return Math.abs(h);
}

function initialsOf(name, email) {
  const src = (name || email || "?").trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return src.slice(0, 2).toUpperCase();
}

export function UserAvatar({ name, email, photoUrl, size = "sm", className = "" }) {
  const sz = SIZE_CLASS[size] || SIZE_CLASS.sm;
  const gradient = GRADIENTS[hash(name || email || "?") % GRADIENTS.length];
  return (
    <Avatar
      className={`${sz} shrink-0 ring-1 ring-white/60 shadow-sm ${className}`}
      data-testid={`user-avatar-${email || name || "anon"}`}
    >
      {photoUrl && <AvatarImage src={photoUrl} alt={name || email || ""} />}
      <AvatarFallback
        className={`bg-gradient-to-br ${gradient} font-semibold text-white`}
      >
        {initialsOf(name, email)}
      </AvatarFallback>
    </Avatar>
  );
}

export default UserAvatar;
